import React from 'react';
import { 
  Container, 
  Typography, 
  Box, 
  Accordion, 
  AccordionSummary, 
  AccordionDetails, 
  Grid, 
  Button, 
  Paper,
  useTheme,
  Fade,
  Grow,
  Zoom,
  useMediaQuery,
  Divider
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Help, ContactSupport, Phone, Email, Chat, LocationOn, Warning, ThumbUp, ThumbDown } from '@mui/icons-material';

// Import images
import helpHero from '../images/x.jpg';
import contactImage from '../images/z.jpg';

const HelpCentre = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const faqs = [
    {
      question: "How do I open an account with Farmers Bank?",
      answer: "You can open an account by visiting any of our branches with your identification documents and proof of address. Alternatively, you can start the process online through our website."
    },
    {
      question: "What should I do if I lose my debit card?",
      answer: "Immediately call our 24/7 customer service line to report the lost card. We will block the card and arrange for a replacement."
    },
    {
      question: "How can I reset my online banking password?",
      answer: "Click on the 'Forgot Password' link on the online banking login page and follow the instructions. You'll need access to your registered email or phone number."
    },
    {
      question: "What are your branch operating hours?",
      answer: "Our branches are typically open from 8:30 AM to 3:30 PM Monday to Friday, and 8:30 AM to 12:00 PM on Saturdays. Some branches may have extended hours."
    },
    {
      question: "How do I apply for an agricultural loan?",
      answer: "Visit any of our branches with your farming business details, or apply online through our agricultural financing portal."
    },
    {
      question: "What security measures do you have for online banking?",
      answer: "We use multi-factor authentication, encryption, and continuous monitoring to protect your online banking experience."
    }
  ];

  const contactMethods = [
    {
      icon: <Phone color="primary" sx={{ fontSize: 40 }} />,
      title: "Phone Support",
      description: "+268 2505 0888",
      subtext: "24/7 Customer Service",
      action: "Call Now"
    },
    {
      icon: <Email color="primary" sx={{ fontSize: 40 }} />,
      title: "Email Us",
      description: "help@farmersbank.co.sz",
      subtext: "Typically respond within 24 hours",
      action: "Send Email"
    },
    {
      icon: <Chat color="primary" sx={{ fontSize: 40 }} />,
      title: "Live Chat",
      description: "Available on our website",
      subtext: "Mon-Fri: 8AM-5PM | Sat: 8AM-12PM",
      action: "Start Chat"
    },
    {
      icon: <LocationOn color="primary" sx={{ fontSize: 40 }} />,
      title: "Branch Visit",
      description: "Find nearest location",
      subtext: "In-person assistance",
      action: "View Branches"
    }
  ];

  return (
    <Box sx={{ overflowX: 'hidden', bgcolor: 'background.default' }}>
      {/* Hero Section */}
      <Box
        sx={{
          backgroundImage: `linear-gradient(rgba(19, 39, 20, 0.85), rgba(19, 39, 20, 0.85)), url(${helpHero})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundAttachment: isMobile ? 'scroll' : 'fixed',
          color: 'white',
          py: 15,
          textAlign: 'center',
          mb: 6,
          position: 'relative',
          '&::before': {
            content: '""',
            position: 'absolute',
            bottom: 0,
            left: 0,
            right: 0,
            height: '100px',
            background: `linear-gradient(to bottom, transparent, ${theme.palette.background.default})`,
            zIndex: 1
          }
        }}
      >
        <Container sx={{ position: 'relative', zIndex: 2 }}>
          <Fade in timeout={800}>
            <Box>
              <Typography 
                variant={isMobile ? 'h3' : 'h2'} 
                gutterBottom 
                sx={{ 
                  fontWeight: 700,
                  lineHeight: 1.2,
                  textShadow: '0 2px 4px rgba(0,0,0,0.3)'
                }}
              >
                How Can We <Box component="span" sx={{ color: theme.palette.secondary.main }}>Help You?</Box>
              </Typography>
              <Typography variant={isMobile ? 'h6' : 'h5'} sx={{ mb: 4, maxWidth: '700px', mx: 'auto' }}>
                Find answers to your questions or connect with our dedicated support team
              </Typography>
              <Zoom in timeout={1000} style={{ transitionDelay: '300ms' }}>
                <Button
                  variant="contained"
                  color="secondary"
                  size="large"
                  endIcon={<ContactSupport />}
                  sx={{
                    px: 6,
                    borderRadius: '50px',
                    boxShadow: '0 4px 20px rgba(0,0,0,0.3)',
                    '&:hover': {
                      transform: 'translateY(-3px)',
                      boxShadow: '0 6px 25px rgba(0,0,0,0.4)'
                    },
                    transition: 'all 0.3s ease',
                  }}
                >
                  Contact Support
                </Button>
              </Zoom>
            </Box>
          </Fade>
        </Container>
      </Box>

      <Container maxWidth="lg" sx={{ py: 5 }}>
        {/* FAQ Section */}
        <Box sx={{ mb: 10 }}>
          <Fade in timeout={800}>
            <Box sx={{ textAlign: 'center', mb: 6 }}>
              <Typography 
                variant="overline" 
                color="primary" 
                sx={{ 
                  fontSize: '1rem', 
                  fontWeight: 600, 
                  letterSpacing: '2px',
                  mb: 2,
                  display: 'inline-block',
                  px: 2,
                  py: 1,
                  bgcolor: 'rgba(87, 166, 73, 0.1)',
                  borderRadius: '50px'
                }}
              >
                QUICK ANSWERS
              </Typography>
              <Typography 
                variant="h3" 
                gutterBottom 
                sx={{ 
                  color: theme.palette.primary.main, 
                  mb: 2,
                  position: 'relative',
                  display: 'inline-block',
                  '&::after': {
                    content: '""',
                    position: 'absolute',
                    bottom: -10,
                    left: '50%',
                    transform: 'translateX(-50%)',
                    width: '80px',
                    height: '4px',
                    bgcolor: theme.palette.secondary.main,
                    borderRadius: '2px'
                  }
                }}
              >
                Frequently Asked Questions
              </Typography>
              <Typography variant="h6" color="text.secondary" sx={{ maxWidth: '700px', mx: 'auto' }}>
                Browse our most common customer inquiries and solutions
              </Typography>
            </Box>
          </Fade>

          <Box sx={{ mt: 4 }}>
            {faqs.map((faq, index) => (
              <Grow in timeout={800 + (index * 100)} key={index}>
                <Accordion
                  elevation={3}
                  sx={{
                    mb: 2,
                    borderRadius: '12px !important',
                    overflow: 'hidden',
                    borderLeft: `4px solid ${theme.palette.secondary.main}`,
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      transform: 'translateY(-3px)',
                      boxShadow: '0 10px 20px rgba(0,0,0,0.1)'
                    },
                    '&:before': {
                      display: 'none'
                    }
                  }}
                >
                  <AccordionSummary
                    expandIcon={<ExpandMoreIcon color="primary" />}
                    sx={{
                      backgroundColor: '#f8fffb',
                      '&:hover': {
                        backgroundColor: 'rgba(87, 166, 73, 0.1)'
                      }
                    }}
                  >
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                      {faq.question}
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails sx={{ backgroundColor: 'white' }}>
                    <Typography variant="body1" sx={{ lineHeight: 1.8, color: 'text.secondary' }}>
                      {faq.answer}
                    </Typography>
                    {index === 0 && (
                      <Button
                        variant="outlined"
                        color="primary"
                        sx={{ 
                          mt: 2, 
                          borderRadius: '50px',
                          '&:hover': {
                            transform: 'translateY(-2px)'
                          },
                          transition: 'all 0.3s ease'
                        }}
                      >
                        Open Account Now
                      </Button>
                    )}
                  </AccordionDetails>
                </Accordion>
              </Grow>
            ))}
          </Box>
        </Box>

        {/* Contact Section */}
        <Grid container spacing={6} sx={{ mb: 10 }}>
          <Grid item xs={12} md={6}>
            <Fade in timeout={800}>
              <Box>
                <Typography 
                  variant="h3" 
                  gutterBottom 
                  sx={{ 
                    color: theme.palette.primary.main,
                    position: 'relative',
                    pb: 2,
                    '&::after': {
                      content: '""',
                      position: 'absolute',
                      bottom: 0,
                      left: 0,
                      width: '80px',
                      height: '4px',
                      bgcolor: theme.palette.secondary.main,
                      borderRadius: '2px'
                    }
                  }}
                >
                  Contact Options
                </Typography>
                <Typography variant="body1" paragraph sx={{ fontSize: '1.1rem', lineHeight: 1.8, color: 'text.primary' }}>
                  Our customer support team is ready to assist you with any questions or concerns about our services. We're committed to providing timely and helpful responses.
                </Typography>
                <Typography variant="body1" paragraph sx={{ fontSize: '1.1rem', lineHeight: 1.8, color: 'text.primary' }}>
                  Choose the most convenient way to reach out to us, and we'll ensure you receive the support you need.
                </Typography>
                <Divider sx={{ my: 4 }} />
                <Typography variant="h6" gutterBottom sx={{ color: theme.palette.primary.main }}>
                  Average Response Times
                </Typography>
                <Box sx={{ display: 'flex', gap: 4 }}>
                  <Box>
                    <Typography variant="body1" sx={{ fontWeight: 600 }}>Phone:</Typography>
                    <Typography variant="body2">Immediate (24/7)</Typography>
                  </Box>
                  <Box>
                    <Typography variant="body1" sx={{ fontWeight: 600 }}>Email:</Typography>
                    <Typography variant="body2">Within 24 hours</Typography>
                  </Box>
                  <Box>
                    <Typography variant="body1" sx={{ fontWeight: 600 }}>Live Chat:</Typography>
                    <Typography variant="body2">Under 5 minutes</Typography>
                  </Box>
                </Box>
              </Box>
            </Fade>
          </Grid>
          <Grid item xs={12} md={6}>
            <Zoom in timeout={800} style={{ transitionDelay: '200ms' }}>
              <Paper
                elevation={6}
                sx={{
                  borderRadius: '16px',
                  overflow: 'hidden',
                  height: '100%',
                  minHeight: 300,
                  position: 'relative',
                  '&:hover': {
                    '&::before': {
                      opacity: 0
                    },
                    '& img': {
                      transform: 'scale(1.05)'
                    }
                  },
                  '&::before': {
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    background: `linear-gradient(45deg, ${theme.palette.primary.main}20, transparent)`,
                    transition: 'opacity 0.3s ease',
                    zIndex: 1
                  }
                }}
              >
                <Box
                  component="img"
                  src={contactImage}
                  alt="Farmers Bank customer service representative"
                  loading="lazy"
                  sx={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                    transition: 'transform 0.5s ease'
                  }}
                />
              </Paper>
            </Zoom>
          </Grid>
        </Grid>

        {/* Contact Methods */}
        <Box sx={{ mb: 10 }}>
          <Grid container spacing={4}>
            {contactMethods.map((method, index) => (
              <Grid item xs={12} sm={6} md={3} key={index}>
                <Grow in timeout={800 + (index * 200)}>
                  <Paper
                    elevation={3}
                    sx={{
                      p: 4,
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      textAlign: 'center',
                      borderRadius: '16px',
                      transition: 'all 0.3s ease',
                      borderTop: `4px solid ${theme.palette.secondary.main}`,
                      '&:hover': {
                        transform: 'translateY(-8px)',
                        boxShadow: '0 15px 30px rgba(0,0,0,0.12)'
                      }
                    }}
                  >
                    <Box
                      sx={{
                        width: 80,
                        height: 80,
                        borderRadius: '50%',
                        backgroundColor: 'rgba(87, 166, 73, 0.1)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        mb: 3,
                        transition: 'all 0.3s ease',
                        '&:hover': {
                          transform: 'rotate(15deg) scale(1.1)',
                          backgroundColor: 'rgba(87, 166, 73, 0.2)'
                        }
                      }}
                    >
                      {method.icon}
                    </Box>
                    <Typography variant="h5" gutterBottom sx={{ fontWeight: 600 }}>
                      {method.title}
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 600, mb: 1, color: 'text.primary' }}>
                      {method.description}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                      {method.subtext}
                    </Typography>
                    <Button
                      variant="contained"
                      color="primary"
                      fullWidth
                      sx={{ 
                        mt: 'auto',
                        borderRadius: '50px',
                        '&:hover': {
                          transform: 'translateY(-2px)'
                        },
                        transition: 'all 0.3s ease'
                      }}
                    >
                      {method.action}
                    </Button>
                  </Paper>
                </Grow>
              </Grid>
            ))}
          </Grid>
        </Box>

        {/* Emergency Section */}
        <Box
          sx={{
            backgroundColor: '#fff8e1',
            borderRadius: '16px',
            p: 6,
            mb: 10,
            borderLeft: `6px solid ${theme.palette.warning.main}`,
            transition: 'all 0.3s ease',
            '&:hover': {
              transform: 'translateY(-3px)',
              boxShadow: '0 10px 20px rgba(0,0,0,0.1)'
            }
          }}
        >
          <Fade in timeout={800}>
            <Grid container spacing={4} alignItems="center">
              <Grid item xs={12} md={8}>
                <Typography variant="h4" gutterBottom sx={{ color: theme.palette.warning.dark, fontWeight: 600 }}>
                  <Warning sx={{ verticalAlign: 'middle', mr: 1, fontSize: '2rem' }} />
                  Emergency Assistance
                </Typography>
                <Typography variant="body1" paragraph sx={{ fontSize: '1.1rem', color: 'text.primary' }}>
                  If you suspect fraudulent activity on your account or need immediate assistance with a security concern, please contact our 24/7 emergency line immediately.
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', mt: 3 }}>
                  <Phone color="error" sx={{ fontSize: 30, mr: 2 }} />
                  <Typography variant="h5" sx={{ color: theme.palette.error.main, fontWeight: 600 }}>
                   +268 2505 0888 (24/7 Emergency)
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} md={4} sx={{ textAlign: 'center' }}>
                <Button
                  variant="contained"
                  color="error"
                  size="large"
                  startIcon={<Warning />}
                  sx={{
                    px: 4,
                    borderRadius: '50px',
                    boxShadow: '0 4px 20px rgba(255, 0, 0, 0.2)',
                    '&:hover': {
                      transform: 'translateY(-3px)',
                      boxShadow: '0 6px 25px rgba(255, 0, 0, 0.3)'
                    },
                    transition: 'all 0.3s ease'
                  }}
                >
                  Report Fraud
                </Button>
              </Grid>
            </Grid>
          </Fade>
        </Box>

        {/* Feedback Section */}
        <Box sx={{ textAlign: 'center', mb: 6 }}>
          <Fade in timeout={800}>
            <Box>
              <Typography 
                variant="h3" 
                gutterBottom 
                sx={{ 
                  color: theme.palette.primary.main,
                  position: 'relative',
                  display: 'inline-block',
                  '&::after': {
                    content: '""',
                    position: 'absolute',
                    bottom: -10,
                    left: '50%',
                    transform: 'translateX(-50%)',
                    width: '80px',
                    height: '4px',
                    bgcolor: theme.palette.secondary.main,
                    borderRadius: '2px'
                  }
                }}
              >
                Was This Helpful?
              </Typography>
              <Typography variant="h5" sx={{ mb: 4, color: 'text.secondary', maxWidth: '700px', mx: 'auto' }}>
                Your feedback helps us improve our help center experience
              </Typography>
              <Box sx={{ display: 'flex', justifyContent: 'center', gap: 3 }}>
                <Button
                  variant="contained"
                  color="primary"
                  size="large"
                  startIcon={<ThumbUp />}
                  sx={{
                    px: 4,
                    borderRadius: '50px',
                    '&:hover': {
                      transform: 'translateY(-3px)',
                      boxShadow: '0 6px 25px rgba(87, 166, 73, 0.3)'
                    },
                    transition: 'all 0.3s ease'
                  }}
                >
                  Yes, Thank You
                </Button>
                <Button
                  variant="outlined"
                  color="primary"
                  size="large"
                  startIcon={<ThumbDown />}
                  sx={{
                    px: 4,
                    borderRadius: '50px',
                    borderWidth: '2px',
                    '&:hover': {
                      borderWidth: '2px',
                      transform: 'translateY(-3px)'
                    },
                    transition: 'all 0.3s ease'
                  }}
                >
                  Needs Improvement
                </Button>
              </Box>
            </Box>
          </Fade>
        </Box>
      </Container>
    </Box>
  );
};

export default HelpCentre;